import * as turf from '@turf/turf';
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import mapboxgl from 'mapbox-gl';
import type { Feature, FeatureCollection, LineString, MultiPolygon, Point, Polygon } from 'geojson';

import {
  AREA_CONVERSION_FACTOR,
  MAP_CONTROLS_POSITION,
  PADDOCK_SOURCE_ID,
} from 'constants/mapConstants';
import type { MapFeature } from './types';

// ── Scale bar ──────────────────────────────────────────────────────────────
// Visual spec ported from Mapbox Maps iOS SDK v11 (MLNScaleBarOrnamentView).
// Colors, lookup table, and dimensions match the native implementation exactly.

/** Primary segment colour — rgb(18, 45, 17) dark green. */
const BAR_PRIMARY = '#122D11';
/** Secondary segment colour — rgb(247, 247, 247) light gray. */
const BAR_SECONDARY = '#F7F7F7';
const BAR_HEIGHT = '0.25rem'; // 4px — exact iOS SDK bar height
const BAR_RADIUS = '0.125rem'; // 2px — pill-shaped ends

/**
 * Metric lookup table from MapboxScaleBarConstants (37 entries, iOS SDK v11).
 * Each entry: [totalMeters, numberOfSegments].
 * Segment counts of 2 or 3 match the native table exactly.
 */
const METRIC_TABLE: ReadonlyArray<readonly [number, number]> = [
  [1, 2], [2, 2], [4, 2], [10, 2], [20, 2], [50, 2], [75, 3],
  [100, 2], [150, 2], [200, 2], [300, 3], [500, 2],
  [1_000, 2], [1_500, 2], [3_000, 3], [5_000, 2],
  [10_000, 2], [20_000, 2], [30_000, 3], [50_000, 2],
  [100_000, 2], [200_000, 2], [300_000, 3], [400_000, 2],
  [500_000, 2], [600_000, 3], [800_000, 2],
  [1_000_000, 2], [2_000_000, 2], [3_000_000, 3], [4_000_000, 2],
  [5_000_000, 2], [6_000_000, 3], [8_000_000, 2],
  [10_000_000, 2], [12_000_000, 2], [15_000_000, 2],
] as const;

/** Minimum rendered px per segment (≈ native 30pt minBarWidth). */
const MIN_SEG_PX = 25;
/** Maximum segment count in the metric table (native max is 3). */
const MAX_BAR_SEGS = 3;

/**
 * Selects the best metric table entry for the current map zoom.
 * Returns the largest total distance whose bar fits within maxWidthPx
 * and has each segment ≥ MIN_SEG_PX; null when zoomed out too far.
 */
function computeSegmentConfig(
  map: mapboxgl.Map,
  maxWidthPx: number,
): { count: number; segMeters: number; totalPx: number } | null {
  const canvas = map.getCanvas();
  const y = canvas.clientHeight / 2;
  const cx = canvas.clientWidth / 2;
  const left = map.unproject([cx - maxWidthPx / 2, y]);
  const right = map.unproject([cx + maxWidthPx / 2, y]);
  const maxMeters = left.distanceTo(right);

  let best: { count: number; segMeters: number; totalPx: number } | null = null;
  for (const [totalMeters, count] of METRIC_TABLE) {
    const totalPx = maxWidthPx * (totalMeters / maxMeters);
    if (totalPx > maxWidthPx) break;
    if (totalPx / count >= MIN_SEG_PX) {
      best = { count, segMeters: totalMeters / count, totalPx };
    }
  }
  return best;
}

/**
 * Formats a cumulative distance for a tick label.
 * Automatically switches m → km at 1 000 m, matching iOS DistanceFormatter.
 */
function formatDistance(meters: number): string {
  if (meters === 0) return '0';
  if (meters >= 1_000) {
    const km = meters / 1_000;
    return `${Number.isInteger(km) ? km : km.toFixed(1)} km`;
  }
  return `${meters} m`;
}

/**
 * CSS applied to every tick label.
 * Replicates the native SDK's 2pt white stroke + black fill drawn via
 * Core Graphics using an 8-point CSS text-shadow in white.
 */
const TICK_LABEL_CSS = [
  'position:absolute',
  'top:0',
  'font-size:0.625rem',
  'font-weight:500',
  'font-family:sans-serif',
  'color:#000',
  'text-shadow:' +
    '-0.0625rem -0.0625rem 0 #fff,0 -0.0625rem 0 #fff,0.0625rem -0.0625rem 0 #fff,' +
    '-0.0625rem 0 0 #fff,0.0625rem 0 0 #fff,' +
    '-0.0625rem 0.0625rem 0 #fff,0 0.0625rem 0 #fff,0.0625rem 0.0625rem 0 #fff',
  'line-height:1',
  'white-space:nowrap',
].join(';');

/**
 * Custom scale bar control.
 * Matches the @rnmapbox/maps native MLNScaleBarOrnamentView used in
 * OpterraPhoenix mobile (scaleBarPosition={{ bottom:0, left:30 }}).
 */
export class CustomScaleControl implements mapboxgl.IControl {
  private _map: mapboxgl.Map | null = null;
  private readonly _container: HTMLDivElement;
  private readonly _labelsRow: HTMLDivElement;
  private readonly _barRow: HTMLDivElement;
  private readonly _segs: readonly HTMLDivElement[];
  private readonly _tickLabels: readonly HTMLSpanElement[];
  private readonly _maxWidthPx = 200;

  constructor() {
    this._container = document.createElement('div');
    this._container.className = 'mapboxgl-ctrl';
    Object.assign(this._container.style, {
      display: 'flex',
      flexDirection: 'column',
      pointerEvents: 'none',
      userSelect: 'none',
      background: 'none',
      border: 'none',
      boxShadow: 'none',
    });

    this._labelsRow = document.createElement('div');
    Object.assign(this._labelsRow.style, {
      position: 'relative',
      height: '0.8125rem',
      marginBottom: '0.125rem',
    });
    const tickLabels: HTMLSpanElement[] = [];
    for (let i = 0; i <= MAX_BAR_SEGS; i++) {
      const span = document.createElement('span');
      span.style.cssText = TICK_LABEL_CSS;
      this._labelsRow.appendChild(span);
      tickLabels.push(span);
    }
    this._tickLabels = tickLabels;

    this._barRow = document.createElement('div');
    Object.assign(this._barRow.style, {
      display: 'flex',
      height: BAR_HEIGHT,
      border: `0.0625rem solid ${BAR_PRIMARY}`,
      borderRadius: BAR_RADIUS,
      boxSizing: 'border-box',
      overflow: 'hidden',
    });
    const segs: HTMLDivElement[] = [];
    for (let i = 0; i < MAX_BAR_SEGS; i++) {
      const seg = document.createElement('div');
      Object.assign(seg.style, {
        height: '100%',
        flexShrink: '0',
        background: i % 2 === 0 ? BAR_PRIMARY : BAR_SECONDARY,
      });
      this._barRow.appendChild(seg);
      segs.push(seg);
    }
    this._segs = segs;

    this._container.appendChild(this._labelsRow);
    this._container.appendChild(this._barRow);
  }

  onAdd(map: mapboxgl.Map): HTMLElement {
    this._map = map;
    map.on('move', this._update);
    return this._container;
  }

  onRemove(): void {
    this._map?.off('move', this._update);
    this._container.parentNode?.removeChild(this._container);
    this._map = null;
  }

  private _update = (): void => {
    if (!this._map) return;
    try {
      const result = computeSegmentConfig(this._map, this._maxWidthPx);
      if (!result) { this._container.style.visibility = 'hidden'; return; }
      this._container.style.visibility = '';

      const { count, segMeters, totalPx } = result;
      const segPx = totalPx / count;

      this._barRow.style.width = `${totalPx}px`;
      this._labelsRow.style.width = `${totalPx}px`;

      this._segs.forEach((seg, i) => {
        seg.style.display = i < count ? '' : 'none';
        if (i < count) seg.style.width = `${segPx}px`;
      });

      this._tickLabels.forEach((lbl, i) => {
        if (i > count) { lbl.style.display = 'none'; return; }
        lbl.style.display = '';
        lbl.textContent = formatDistance(i * segMeters);
        lbl.style.left = `${i * segPx}px`;
        lbl.style.transform = 'translateX(-50%)';
      });
    } catch {
      // unproject may throw before the map style is fully initialised
    }
  };
}

// ── End scale bar ──────────────────────────────────────────────────────────

/**
 * Converts an array of map features to a GeoJSON FeatureCollection.
 */
export function featuresToFeatureCollection(
  features: MapFeature[]
): FeatureCollection<Polygon> {
  return {
    type: 'FeatureCollection',
    features: features.map<Feature<Polygon>>((f) => ({
      type: 'Feature',
      id: f.id,
      geometry: f.geometry,
      properties: {
        id: f.id,
        name: f.name,
        area: f.area,
      },
    })),
  };
}

/**
 * Builds a GeoJSON FeatureCollection of Point features, one per map feature,
 * positioned at the turf centroid of each polygon.
 * Used as the source for the labels symbol layer.
 */
export function featuresToLabelPointCollection(
  features: MapFeature[],
  selectedIds: Set<string> = new Set()
): FeatureCollection<Point> {
  return {
    type: 'FeatureCollection',
    features: features.map<Feature<Point>>((f) => {
      const center = turf.centroid(turf.polygon(f.geometry.coordinates));
      return {
        type: 'Feature',
        geometry: center.geometry,
        properties: {
          id: f.id,
          name: f.name,
          area: f.area,
          isSelected: selectedIds.has(f.id),
        },
      };
    }),
  };
}

export function calculateAreaInHectares(feature: Feature<Polygon>): number {
  return Number((turf.area(feature) / AREA_CONVERSION_FACTOR).toFixed(2));
}

/**
 * Deselects all features on the map by setting their feature state to unselected.
 */
export function deselectAllFeatures(
  map: mapboxgl.Map,
  featureCollection: FeatureCollection<Polygon>
): void {
  featureCollection.features.forEach((feature) => {
    const featureId = feature.properties?.id;
    if (featureId) {
      map.setFeatureState(
        { source: PADDOCK_SOURCE_ID, id: featureId },
        { selected: false }
      );
    }
  });
}

/**
 * Gets the paddock GeoJSON source from the map.
 */
export function getPaddockSource(
  map: mapboxgl.Map | null
): mapboxgl.GeoJSONSource | null {
  if (!map) return null;
  return map.getSource(PADDOCK_SOURCE_ID) as mapboxgl.GeoJSONSource | null;
}

/**
 * Sets up standard map controls (scale bar, draw control).
 * Navigation and geolocate are provided by the React MapNavToolbar component.
 */
export function setupMapControls(map: mapboxgl.Map, draw: MapboxDraw): void {
  map.addControl(new CustomScaleControl(), 'bottom-left');
  map.addControl(draw, MAP_CONTROLS_POSITION.topLeft);
}

/**
 * Merges multiple polygon geometries using Turf union.
 * Returns a single Polygon if all polygons form one contiguous area.
 * Returns null if the result is a MultiPolygon (non-adjacent).
 */
export function mergePaddockGeometriesMultiple(
  polygons: Polygon[]
): Polygon | null {
  if (polygons.length < 2) return null;
  const features = polygons.map((p) => turf.polygon(p.coordinates));
  const unioned = turf.union(turf.featureCollection(features));
  if (!unioned || unioned.geometry.type === 'MultiPolygon') return null;
  return unioned.geometry as Polygon;
}

/**
 * Splits a polygon by a LineString using half-plane clipping via turf.intersect.
 */
export function splitPolygonByLine(
  polygonFeature: Feature<Polygon>,
  lineFeature: Feature<LineString>
): Feature<Polygon>[] | null {
  const outerRing = polygonFeature.geometry.coordinates[0];
  if (!outerRing || outerRing.length < 4) return null;

  const outerOnly: Feature<Polygon> = {
    type: 'Feature',
    geometry: { type: 'Polygon', coordinates: [outerRing] },
    properties: {},
  };
  const outerBoundary = turf.polygonToLine(outerOnly);
  if (turf.lineIntersect(outerBoundary, lineFeature).features.length < 2) return null;

  const bbox = turf.bbox(polygonFeature);
  const diag =
    Math.sqrt(Math.pow(bbox[2] - bbox[0], 2) + Math.pow(bbox[3] - bbox[1], 2)) *
    100;

  const lineCoords = lineFeature.geometry.coordinates;
  const [x1, y1] = lineCoords[0];
  const [x2, y2] = lineCoords[lineCoords.length - 1];
  const dx = x2 - x1;
  const dy = y2 - y1;
  const len = Math.sqrt(dx * dx + dy * dy);
  if (len === 0) return null;

  const ux = dx / len;
  const uy = dy / len;
  const nx = -uy;
  const ny = ux;

  const ex1 = [x1 - ux * diag, y1 - uy * diag];
  const ex2 = [x2 + ux * diag, y2 + uy * diag];

  const leftRect = turf.polygon([[
    [ex1[0] + nx * diag, ex1[1] + ny * diag],
    [ex2[0] + nx * diag, ex2[1] + ny * diag],
    [...ex2],
    [...ex1],
    [ex1[0] + nx * diag, ex1[1] + ny * diag],
  ]]);

  const rightRect = turf.polygon([[
    [...ex1],
    [...ex2],
    [ex2[0] - nx * diag, ex2[1] - ny * diag],
    [ex1[0] - nx * diag, ex1[1] - ny * diag],
    [...ex1],
  ]]);

  const leftResult = turf.intersect(
    turf.featureCollection([polygonFeature, leftRect])
  );
  const rightResult = turf.intersect(
    turf.featureCollection([polygonFeature, rightRect])
  );

  if (!leftResult || !rightResult) return null;

  const flatten = (f: Feature<Polygon | MultiPolygon>): Feature<Polygon>[] => {
    if (f.geometry.type === 'Polygon') return [f as Feature<Polygon>];
    return (f.geometry as MultiPolygon).coordinates.map((coords) => ({
      type: 'Feature' as const,
      geometry: { type: 'Polygon' as const, coordinates: coords },
      properties: {},
    }));
  };

  const pieces = [
    ...flatten(leftResult as Feature<Polygon | MultiPolygon>),
    ...flatten(rightResult as Feature<Polygon | MultiPolygon>),
  ].filter((p) => turf.area(p) >= 1);

  return pieces.length >= 2 ? pieces : null;
}

/**
 * Splits a polygon by a polyline (zigzag line with multiple segments).
 */
export function splitPolygonByPolyline(
  polygonFeature: Feature<Polygon>,
  lineFeature: Feature<LineString>
): Feature<Polygon>[] | null {
  const coords = lineFeature.geometry.coordinates;
  if (!coords || coords.length < 2) return null;

  let parts: Feature<Polygon>[] = [
    {
      type: 'Feature',
      geometry: polygonFeature.geometry,
      properties: {},
    },
  ];

  for (let i = 0; i < coords.length - 1; i++) {
    const segmentFeature: Feature<LineString> = {
      type: 'Feature',
      geometry: {
        type: 'LineString',
        coordinates: [coords[i], coords[i + 1]],
      },
      properties: {},
    };

    let split = false;
    for (let p = 0; p < parts.length; p++) {
      const result = splitPolygonByLine(parts[p], segmentFeature);
      if (result) {
        parts = [...parts.slice(0, p), ...result, ...parts.slice(p + 1)];
        split = true;
        break;
      }
    }
    if (!split && i === 0) return null;
  }

  return parts.length >= 2 ? parts : null;
}
